/**
 * client/src/lib/use-current-user.ts
 *
 * Centralized hook for fetching the authenticated user via /api/auth/me.
 *
 * Single source of truth — call sites used to redefine the same TanStack
 * Query config. The queryFn (401/403 → null catch, plus a bootstrap timeout)
 * now lives in the shared `auth-me.ts`; retry is left to the resilient global
 * policy (never retries 401/403, retries transient failures). If the auth/me
 * contract changes, fix it there and every consumer follows.
 *
 * The queryKey stays as ["auth/me"] so existing
 *   queryClient.invalidateQueries({ queryKey: ["auth/me"] })
 * callers (e.g. auth-entry/index.tsx) keep working.
 */

import { useQuery } from "@tanstack/react-query";
import { fetchAuthMe, AUTH_ME_QUERY_KEY } from "./auth-me";
import type { CurrentUser } from "@/App";

interface UseCurrentUserOptions {
  /**
   * Override the default 30s staleTime — e.g. `Infinity` for feature-flag
   * evaluation where we only need user identity once per session.
   */
  staleTime?: number;
}

export function useCurrentUser(options?: UseCurrentUserOptions) {
  const { data, isLoading, isError, refetch } = useQuery<CurrentUser | null>({
    queryKey: AUTH_ME_QUERY_KEY,
    queryFn: fetchAuthMe,
    staleTime: options?.staleTime ?? 30_000,
    // `retry` is intentionally NOT set: the query inherits the resilient global
    // policy (queryClient.ts) — never retries 401/403, retries transient
    // network/timeout/5xx up to 2×. The prior `retry: false` here meant a single
    // hung/failed bootstrap fetch stranded the app on a permanent PageLoader
    // with no recovery; the 401/403 short-circuit already lives in fetchAuthMe,
    // so disabling retry bought nothing but the hang.
  });

  // `data` preserves the 3-state semantics of the underlying useQuery:
  //   - CurrentUser  → authenticated
  //   - null         → 401/403 (intentionally logged-out)
  //   - undefined    → still loading OR errored (use isLoading/isError)
  // Coercing undefined → null would conflate logout with transient error and
  // wrongly trigger "redirect to /auth" branches in consumers (#503-follow-up).
  return {
    user: data,
    isLoading,
    isError,
    refetch,
  };
}
